import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLang } from "@src/hooks/useLang";
import { jdCallbackError } from "./johnDeereCallbackError";

/**
 * Handles John Deere OAuth callback query params (?deere_connected=1 or ?deere_error=...).
 * Shows a toast and clears the params from the URL.
 */
const JohnDeereCallbackHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const jdLang = useLang().johnDeere;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get("deere_connected");
    const error = params.get("deere_error");

    if (connected === "1" || error) {
      if (handledRef.current) return;
      handledRef.current = true;

      if (connected === "1") {
        toast.success("Successfully connected to John Deere Operations Center");
      }
      if (error) {
        // A code the customer can act on gets its own message.
        const known = jdCallbackError(error);
        // The param is attacker-controllable (anyone can craft the URL), so only
        // surface it when it looks like a backend error code, not free-form text.
        const isErrorCode = /^[\w .:-]{1,120}$/.test(error);
        toast.error(
          known
            ? jdLang[known.key] ?? known.fallback
            : isErrorCode
              ? `Failed to connect to John Deere: ${error}`
              : "Failed to connect to John Deere Operations Center"
        );
      }

      params.delete("deere_connected");
      params.delete("deere_error");
      const newSearch = params.toString();
      const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      navigate(newPath, { replace: true });
    } else {
      handledRef.current = false;
    }
  }, [location.search, location.pathname, navigate, jdLang]);

  return null;
};

export default JohnDeereCallbackHandler;
